import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  useReceitaDoDav, SalvarReceitaMedicaRequest, DavPendenteReceita,
} from "@/features/anvisa/hooks/useReceitaMedica";

const CONSELHOS = ["CRM", "CRMV", "CRO", "COREN", "CRF", "RMS"];

function hojeIso() { return new Date().toISOString().slice(0, 10); }

const EMPTY: SalvarReceitaMedicaRequest = {
  tipoReceituario: 5, numeroNotificacao: "", dtPrescricao: hojeIso(), usoMedicamento: 1,
  prescritorNome: "", prescritorRegistro: "", prescritorConselho: "CRM", prescritorUf: "",
  compradorNome: "", compradorTipoDocumento: null, compradorNumeroDocumento: "",
  compradorOrgaoExpedidor: "", compradorUf: "",
  pacienteNome: "", pacienteIdade: null, pacienteUnidadeIdade: 1, pacienteSexo: null,
  pacienteCid: "", usoProlongado: "",
};

interface Props {
  dav: DavPendenteReceita;
  open: boolean;
  onClose: () => void;
}

export default function ReceitaDialog({ dav, open, onClose }: Props) {
  const { receita, isLoading, salvarReceita, salvando } = useReceitaDoDav(dav.movDavId);
  const [form, setForm] = useState<SalvarReceitaMedicaRequest>(EMPTY);

  useEffect(() => {
    if (receita) {
      setForm({
        tipoReceituario: receita.tipoReceituario,
        numeroNotificacao: receita.numeroNotificacao ?? "",
        dtPrescricao: receita.dtPrescricao?.slice(0, 10) ?? hojeIso(),
        usoMedicamento: receita.usoMedicamento,
        prescritorNome: receita.prescritorNome ?? "",
        prescritorRegistro: receita.prescritorRegistro ?? "",
        prescritorConselho: receita.prescritorConselho ?? "CRM",
        prescritorUf: receita.prescritorUf ?? "",
        compradorNome: receita.compradorNome ?? "",
        compradorTipoDocumento: receita.compradorTipoDocumento ?? null,
        compradorNumeroDocumento: receita.compradorNumeroDocumento ?? "",
        compradorOrgaoExpedidor: receita.compradorOrgaoExpedidor ?? "",
        compradorUf: receita.compradorUf ?? "",
        pacienteNome: receita.pacienteNome ?? "",
        pacienteIdade: receita.pacienteIdade ?? null,
        pacienteUnidadeIdade: receita.pacienteUnidadeIdade ?? 1,
        pacienteSexo: receita.pacienteSexo ?? null,
        pacienteCid: receita.pacienteCid ?? "",
        usoProlongado: receita.usoProlongado ?? "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [receita]);

  const set = <K extends keyof SalvarReceitaMedicaRequest>(k: K, v: SalvarReceitaMedicaRequest[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const antimicrobiano = form.tipoReceituario === 5;
  const controleEspecial = !antimicrobiano;

  const handleSalvar = async () => {
    try {
      await salvarReceita(form);
      onClose();
    } catch { /* toast tratado no hook */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receita médica — DAV {dav.numeroDav}</DialogTitle>
          <DialogDescription>{dav.clienteNome} · {dav.qtdItensControlados} item(ns) controlado(s)</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground text-sm">Carregando receita...</div>
        ) : (
          <div className="space-y-5">
            {/* Dados da receita */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Tipo de receituário *</Label>
                <Select value={String(form.tipoReceituario)} onValueChange={(v) => set("tipoReceituario", Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Controle Especial 2 vias (Branca)</SelectItem>
                    <SelectItem value="2">Notificação de Receita B (Azul)</SelectItem>
                    <SelectItem value="3">Notificação de Receita Especial (Branca)</SelectItem>
                    <SelectItem value="4">Notificação de Receita A (Amarela)</SelectItem>
                    <SelectItem value="5">Antimicrobiano 2 vias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {controleEspecial && (
                <div className="space-y-1">
                  <Label>Nº da notificação</Label>
                  <Input maxLength={16} value={form.numeroNotificacao ?? ""}
                    onChange={(e) => set("numeroNotificacao", e.target.value)} />
                </div>
              )}
              <div className="space-y-1">
                <Label>Data de prescrição *</Label>
                <Input type="date" value={form.dtPrescricao} onChange={(e) => set("dtPrescricao", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Uso do medicamento</Label>
                <Select value={String(form.usoMedicamento)} onValueChange={(v) => set("usoMedicamento", Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Humano</SelectItem>
                    <SelectItem value="2">Veterinário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prescritor */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-sm font-semibold">Prescritor (obrigatório)</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1 lg:col-span-2">
                  <Label>Nome *</Label>
                  <Input value={form.prescritorNome} onChange={(e) => set("prescritorNome", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Registro *</Label>
                  <Input value={form.prescritorRegistro} onChange={(e) => set("prescritorRegistro", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>Conselho</Label>
                    <Select value={form.prescritorConselho} onValueChange={(v) => set("prescritorConselho", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONSELHOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>UF *</Label>
                    <Input maxLength={2} value={form.prescritorUf}
                      onChange={(e) => set("prescritorUf", e.target.value.toUpperCase())} />
                  </div>
                </div>
              </div>
            </div>

            {/* Comprador (controle especial) */}
            {controleEspecial && (
              <div className="space-y-2 border-t pt-3">
                <h4 className="text-sm font-semibold">Comprador (obrigatório p/ controle especial)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="space-y-1 lg:col-span-2">
                    <Label>Nome *</Label>
                    <Input value={form.compradorNome ?? ""} onChange={(e) => set("compradorNome", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Tipo de documento</Label>
                    <Select value={String(form.compradorTipoDocumento ?? 0)}
                      onValueChange={(v) => set("compradorTipoDocumento", Number(v) === 0 ? null : Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">— Nenhum —</SelectItem>
                        <SelectItem value="2">Carteira de Identidade (RG)</SelectItem>
                        <SelectItem value="1">Carteira Profissional</SelectItem>
                        <SelectItem value="13">Passaporte</SelectItem>
                        <SelectItem value="50">CTPS</SelectItem>
                        <SelectItem value="62">CNPJ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Nº do documento</Label>
                    <Input value={form.compradorNumeroDocumento ?? ""} onChange={(e) => set("compradorNumeroDocumento", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Órgão expedidor</Label>
                    <Input value={form.compradorOrgaoExpedidor ?? ""} onChange={(e) => set("compradorOrgaoExpedidor", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>UF</Label>
                    <Input maxLength={2} value={form.compradorUf ?? ""}
                      onChange={(e) => set("compradorUf", e.target.value.toUpperCase())} />
                  </div>
                </div>
              </div>
            )}

            {/* Paciente (antimicrobiano) */}
            {antimicrobiano && (
              <div className="space-y-2 border-t pt-3">
                <h4 className="text-sm font-semibold">Paciente (obrigatório p/ antimicrobiano)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1 lg:col-span-2">
                    <Label>Nome *</Label>
                    <Input value={form.pacienteNome ?? ""} onChange={(e) => set("pacienteNome", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label>Idade</Label>
                      <Input type="number" min="0" value={form.pacienteIdade ?? ""}
                        onChange={(e) => set("pacienteIdade", e.target.value === "" ? null : Number(e.target.value))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Unidade</Label>
                      <Select value={String(form.pacienteUnidadeIdade ?? 1)} onValueChange={(v) => set("pacienteUnidadeIdade", Number(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Anos</SelectItem>
                          <SelectItem value="2">Meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Sexo</Label>
                    <Select value={String(form.pacienteSexo ?? 0)}
                      onValueChange={(v) => set("pacienteSexo", Number(v) === 0 ? null : Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">—</SelectItem>
                        <SelectItem value="1">Masculino</SelectItem>
                        <SelectItem value="2">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>CID-10</Label>
                    <Input maxLength={4} value={form.pacienteCid ?? ""}
                      onChange={(e) => set("pacienteCid", e.target.value.toUpperCase())} />
                  </div>
                  <div className="space-y-1">
                    <Label>Uso prolongado</Label>
                    <Select value={form.usoProlongado || "0"}
                      onValueChange={(v) => set("usoProlongado", v === "0" ? "" : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">— N/A —</SelectItem>
                        <SelectItem value="S">Sim</SelectItem>
                        <SelectItem value="N">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="button" onClick={handleSalvar} disabled={salvando || isLoading}>
            {salvando ? "Salvando..." : "Salvar receita"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
